'use strict';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as GC from '@grapecity/spread-sheets';
import { clone, flat, getUnique } from '@helpers/arrays';
import { backoff } from '@helpers/backoff';
import { compareKeys } from '@helpers/object-sort';
import * as spreadHelper from '@helpers/spread/cell-styling';
import { capitalize } from '@helpers/strings';
import { LookupData, SpreadLookupInfo } from '@models/lookup-data';
import { LookupSetup, TableType } from '@models/lookup-setup';
import { ResultsetItem } from '@models/resultset-item';
import { Scenario } from '@models/scenario';
import { AppConfigAPI } from '@models/types/appconfig';
import { AppConfigProvider } from '@root/app/app.config.provider';
import { OpenIdConnectService } from '@services/auth/open-id-connect.service';
import { ResultTabsService } from '@services/results/result-tabs.service';
import { ScenarioService } from '@services/scenario/scenario.service';
import { TrackingService } from '@services/tracking/tracking.service';
import { BehaviorSubject, forkJoin, Subject } from 'rxjs';
import { catchError, debounceTime, map, tap } from 'rxjs/operators';
import { TableSetupService } from './table-setup.service';

@Injectable({ providedIn: 'root' })
export class TableDataService {
  private environment: AppConfigAPI;

  // Scenario/Global Switch
  public scenarioTables: LookupSetup[] = [];
  public globalTables: LookupSetup[] = [];

  // private user_id: number;
  private lastNonEmptyRow = 0;
  public lookupDataLoaded$ = new BehaviorSubject(false);
  public scenarioSwitchState$: BehaviorSubject<TableType> = new BehaviorSubject<TableType>('scenario');
  public filterTree$ = new BehaviorSubject('scenario');
  public dimensionedByScenarioTables = [];

  public lookupTables$: Subject<LookupSetup[]> = new Subject<LookupSetup[]>();

  set lookupTables(tables: LookupSetup[]) {
    this.scenarioTables = tables.filter(table => table.DimensionList.map(dimension => dimension.DimMnemonic).includes('ScenarioMn'));
    this.globalTables = tables.filter(table => !table.DimensionList.map(dimension => dimension.DimMnemonic).includes('ScenarioMn'));

    this.lookupTables$.next(tables);
  }

  public get scenarioSwitchState(): TableType {
    return this.scenarioSwitchState$.getValue();
  }
  public set scenarioSwitchState(newVal: TableType) {
    const switchChanged = newVal !== this.scenarioSwitchState$.getValue();

    if (switchChanged) {
      this.scenarioSwitchState$.next(newVal);
    }
  }

  constructor(
    private http: HttpClient,
    private auth: OpenIdConnectService,
    private tableSetupService: TableSetupService,
    private scenarioService: ScenarioService,
    private resultTabsService: ResultTabsService,
    private trackingService: TrackingService,
    private appConfig: AppConfigProvider
  ) {
    this.appConfig.environment.subscribe(env => {
      this.environment = env.API;
    });
  }

  public showInScenarioGlobalToggleCurrentView(table: LookupSetup) {
    const isTableDimensionedByScenario =
      (table.DimensionList &&
        table.DimensionList.length > 0 &&
        table.DimensionList.map(dimension => dimension.DimMnemonic).includes('ScenarioMn')) ||
      false;
    if (isTableDimensionedByScenario) {
      this.dimensionedByScenarioTables.push(table.Mnemonic.toLowerCase());
    }
    return this.scenarioSwitchState === 'scenario' ? isTableDimensionedByScenario : !isTableDimensionedByScenario;
  }

  save(table_id?: number) {
    const url = `${this.environment.LOOKUPAPI}/api/lookup/updatedata`;
    let body: any = {
      user_id: -1,
      return_required: 1,
    };

    if (table_id) {
      body = {
        ...body,
        table_id,
      };
    }

    return this.http.post(url, body).pipe(
      debounceTime(250),
      backoff(3, 250),
      catchError(err => {
        console.error(`Problem saving lookup edit tracking data \nStacktrace: ${err}`);
        return err;
      }),
      tap(res => {
        this.trackingService.updateModelDataChangesFlags('lookupTables', false, `save lookup edit tracking ${res}`);
      })
    );
  }

  public isTableDimensionedByScenarioMn = (table: LookupSetup) =>
    (table.DimensionList &&
      table.DimensionList.length > 0 &&
      table.DimensionList.map(dimension => dimension.DimMnemonic).includes('ScenarioMn')) ||
    false;

  discardLookupTracking(table_id: number = null) {
    const url = `${this.environment.TRACKINGAPI}/api/lookup/deletetracking`;

    const user_id = this.auth.userId;
    const body = {
      user_id,
      table_id: table_id || -1,
    };

    return this.http.post(url, body).pipe(
      debounceTime(250),
      backoff(3, 250),
      catchError(err => {
        console.error('Something happened while discarding temporary changes in lookup data', err);
        return err;
      })
    );
  }

  updateLookupTracking(query: LookupData[]) {
    const url = `${this.environment.TRACKINGAPI}/api/lookup/updatetracking`;
    const minifiedQuery = this.minify(query);
    const mnemonics_ids = new Set(minifiedQuery.map(q => q.i));

    const bodies = [];

    mnemonics_ids.forEach(mnemonic_id => {
      const tables = minifiedQuery.filter(t => t.i === mnemonic_id);

      // Removing redundant data
      tables.forEach(t => {
        delete t.m;
        delete t.i;
      });

      const mnemonicBody = {
        mnemonic: mnemonic_id === Number(mnemonic_id) ? Number(mnemonic_id) : mnemonic_id,
        json: tables,
      };
      bodies.push(mnemonicBody);
    });

    const user_id = this.auth.userId;
    const body = {
      user_id,
      // tslint:disable-next-line:quotemark
      json: JSON.stringify(bodies).replace(/\'/g, "''"),
    };

    return this.http.post(url, body).pipe(
      // debounceTime(250),
      backoff(3, 250),
      map(response => {
        const resp = JSON.parse(JSON.stringify(response));
        return resp.map(item => {
          item.json = this.deminify(JSON.parse(item.json));
          return item;
        });
      }),
      catchError(err => {
        console.error(`Problem updating lookup edit tracking data \nStacktrace: ${err}`);
        return err;
      }),
      tap(response => {
        if (response) {
          this.trackingService.updateModelDataChangesFlags('lookupTables', true, `updateLookupTracking: ${response}`);
        }
      })
    );
  }

  private getMnemonicsToShow(resultTabs: ResultsetItem[]) {
    const baselineTabsToShow = resultTabs.filter(t => !t.Hidden).map(x => x.Mnemonic);
    const stressTabsToShow = flat(resultTabs.map(s => s.StressList))
      .filter(s => !s.Hidden)
      .map(s => s.Scenario_Mnemonic);

    const tabsToShow = [...baselineTabsToShow, ...stressTabsToShow];
    return tabsToShow;
  }

  public fetchLookupTables() {
    return this.tableSetupService.getLookupTablesWithData().pipe(
      debounceTime(250),
      backoff(3, 250),
      tap((tables: LookupSetup[]) => {
        this.lookupTables = tables;
      })
    );
    // .toPromise();
  }

  public async getLookupTablesSheet(toExport = false) {
    const sheet: GC.Spread.Sheets.Worksheet = new GC.Spread.Sheets.Worksheet('Lookup Tables');
    const spreadSheetData: SpreadLookupInfo[] = [];

    sheet.options.isProtected = false;
    sheet.setColumnResizable(0, true, GC.Spread.Sheets.SheetArea.colHeader);
    // sheet.getRange(1,-1, 1, -1, GC.Spread.Sheets.SheetArea.viewport).resizable(true);

    sheet.suspendPaint();
    sheet.suspendEvent();
    sheet.suspendCalcService();

    let tables: LookupSetup[] = [];
    let scenarios: Scenario[] = [];
    const resultTabs: ResultsetItem[] = this.resultTabsService.tabCollection$.value;

    return forkJoin([this.tableSetupService.getLookupTablesWithData(), this.scenarioService.getScenarios()])
      .pipe(
        catchError(err => {
          console.warn(err);
          return err;
        }),
        backoff(3, 250)
      )
      .toPromise()
      .then(data => {
        try {
          tables = data[0] as LookupSetup[];
          scenarios = data[1] as Scenario[];

          // Filter tables by opened tabs
          const scenarioMnemonicsToShow = this.getMnemonicsToShow(resultTabs);

          this.prepareData(tables, scenarios, scenarioMnemonicsToShow);

          tables.forEach((table, tableIndex) => {
            let info: SpreadLookupInfo = null;
            // Add 1 and 2 Dimension tables
            if (table.Dimensions < 3) {
              info = this.addTableToSpreadsheet(sheet, table);
              spreadSheetData.push(info);
            } else if (table.Dimensions === 3) {
              // Add 3 Dimension tables
              const thirdDimensionReference = table.references[2];
              const thirdDimensionMnemonic = table.dimensions[2];

              // Get 3rd dimension titles
              const thirdDimensionTitles = table.Data.map(d => d[thirdDimensionReference])
                .map(d => (d = d === Number(d) ? Number(d) : d))
                .filter(getUnique);

              // For empty 3D tables to appear in spread sheet
              if (thirdDimensionTitles.length === 0) {
                thirdDimensionTitles.push(null);
              }

              // Getting results size
              let resultsSize = 0;
              thirdDimensionTitles.forEach(thirdDimension => {
                const dataSlice = table.Data.filter(row => row[thirdDimensionReference] === thirdDimension);
                if (dataSlice.length > resultsSize) {
                  resultsSize = dataSlice.length;
                }
              });

              // Adding one 2D table for each 3rd dimension
              thirdDimensionTitles.forEach(thirdDimension => {
                const dataSlice = table.Data.filter(row => row[thirdDimensionReference] === thirdDimension);
                // Empty 3D tables won't show up after navigating back
                // All pivots for this mnemonic should have same-sized results
                if (dataSlice.length === resultsSize) {
                  const tableSlice = { ...table, Data: dataSlice };
                  info = this.addTableToSpreadsheet(sheet, tableSlice, thirdDimension);
                  info.allThirdDimensions = thirdDimensionTitles; // .filter(x => x !== thirdDimension);
                  spreadSheetData.push(info);
                }
              });
            }
          });

          spreadSheetData.forEach(table => spreadHelper.drawTable(table, sheet, false, true, toExport));
          // ! Fix 4718:
          // -- Reset of the flag for the startingRow once all tables are drawn
          this.lastNonEmptyRow = 0;

          // Auto fit
          // if (tables.length) {
          //   const cols = sheet.getColumnCount();
          //   for (let col = 0; col < cols - 2; col++) {
          //     spreadHelper.autoFitColumn(sheet, col);
          //   }
          // }

          // Autofit columns before returning sheet
          // const cols = sheet.getColumnCount();
          // for (let col = 0; col < cols; col++) {
          //   spreadHelper.autoFitColumn(sheet, col);
          // }

          return sheet;
        } catch (err) {
          console.warn(err);
        }
      });
  }

  private addTableToSpreadsheet(sheet: GC.Spread.Sheets.Worksheet, table: LookupSetup, thirdDimensionTitle = null): SpreadLookupInfo {
    const data = table.Data;
    const references = table.references;
    const dimMnemonics = table.dimensions;

    const d1Reference = references[0];
    const d2Reference = references[1] || null;
    const d3Reference = references[2] || null;
    const d1Mnemonic = dimMnemonics[0];
    const d2Mnemonic = dimMnemonics[1] || null;
    const d3Mnemonic = dimMnemonics[2] || null;

    let columnTitles = [];
    const startingRow = this.lastNonEmptyRow;
    const isBalanceAssumption = table.DataTableType.toLowerCase() === 'balancesheet';
    const isCommitDraw = table.DataTableType.toLowerCase() === 'commitdraw';
    // const editLock = !this.selectedTreeItem.value;
    const listOfThirdDimensions: any[] = [];

    // Fill Row Titles
    const rowTitles = data
      .map(d => d[d1Reference])
      .map(d => (d = d === Number(d) ? Number(d) : d))
      .filter(getUnique)
      .sort((a, b) => a - b);

    const numberOfRows = rowTitles.length;
    let numberOfColumns = 0;
    this.lastNonEmptyRow += numberOfRows + 4;

    // Adding rows to spreadsheet so it have space for the new inserted table
    sheet.addRows(sheet.getRowCount(), numberOfRows + 4);

    sheet.setArray(startingRow + 2, 0, rowTitles);

    // Fill Column Titles
    if (references.length > 1) {
      columnTitles = data
        .map(d => d[d2Reference])
        .map(d => (d = d === Number(d) ? Number(d) : d))
        .filter(getUnique)
        .sort((a, b) => a - b);
      numberOfColumns = columnTitles.length;
      // Add columns to spreadsheet if needed
      const totalColumnsNeeded = numberOfColumns + 3; // Row headers + table columns + 2 padding rows
      if (sheet.getColumnCount() < totalColumnsNeeded) {
        sheet.addColumns(sheet.getColumnCount(), totalColumnsNeeded - sheet.getColumnCount());
      }
      sheet.setArray(startingRow + 1, 1, [columnTitles]);
    } else {
      numberOfColumns = 1;
    }

    // Fill results with proper row alignment
    if (table.Dimensions === 1) {
      // For 1D tables, ensure values match row order
      const values = rowTitles.map(rowTitle => {
        const matchingData = data.find(d => d[d1Reference] === rowTitle);
        return matchingData ? matchingData.Result : null;
      });
      sheet.setArray(startingRow + 2, 1, values);
    } else {
      // For 2D+ tables, ensure values match row order for each column
      columnTitles.forEach((col, index) => {
        const values = rowTitles.map(rowTitle => {
          const matchingData = data.find(d => d[d1Reference] === rowTitle && d[d2Reference] === col);
          return matchingData ? matchingData.Result : null;
        });
        sheet.setArray(startingRow + 2, 1 + index, values);
      });
    }

    let toBeDeleted = false;
    const toBeDeletedRows = [];
    const toBeDeletedCols = [];

    toBeDeleted = table.Dimensions > 2 && table.Data.every(x => x.toBeDeleted);
    if (!toBeDeleted) {
      rowTitles.forEach((r, i) => {
        const toBeDeletedRow = table.Data.filter(d => d[d1Reference] === r);
        const allDeleted = toBeDeletedRow.every(x => x.toBeDeleted);

        if (toBeDeletedRow.length > 0 && allDeleted) {
          toBeDeletedRows.push(i);
        }
      });

      if (table.Dimensions > 1) {
        columnTitles.forEach((c, i) => {
          const toBeDeletedCol = table.Data.filter(d => d[d2Reference] === c);
          const allDeleted = toBeDeletedCol.every(x => x.toBeDeleted);
          if (toBeDeletedCol.length > 0 && allDeleted) {
            toBeDeletedCols.push(i);
          }
        });
      }
    }

    const mnemonicCell = sheet.getCell(startingRow, 0);
    const subtitleCell = sheet.getCell(startingRow, 1);
    const pivotCell = sheet.getCell(startingRow + 1, 0);
    mnemonicCell.locked(true);
    subtitleCell.locked(true);
    pivotCell.locked(true);

    const firstColumn = sheet.getRange(startingRow + 2, 0, numberOfRows, 1);
    const titleRow = sheet.getRange(startingRow + 1, 1, 1, columnTitles.length);
    const resultsRange = sheet.getRange(startingRow + 2, 1, numberOfRows, numberOfColumns);

    if (isBalanceAssumption || isCommitDraw) {
      spreadHelper.setImmutableStyle(firstColumn);
      spreadHelper.setImmutableStyle(titleRow);
      spreadHelper.setImmutableStyle(pivotCell);
    }

    // Returning Spread Info object
    const spreadInfo: SpreadLookupInfo = {
      startRow: startingRow,
      startCol: 0,
      endRow: startingRow + rowTitles.length + 1,
      endCol: 0 + numberOfColumns,
      mnemonic: table.Mnemonic,
      id: table.Id,
      rows: rowTitles,
      numberOfRows: rowTitles.length,
      cols: columnTitles,
      numberOfColumns,
      thirdDimension: thirdDimensionTitle,
      allThirdDimensions: listOfThirdDimensions,
      references,
      dimMnemonics,
      results: sheet.getArray(startingRow + 2, 1, rowTitles.length, numberOfColumns, false),
      category: table.DataTableType,
      toBeDeleted: toBeDeleted,
      toBeDeletedRows: clone(toBeDeletedRows),
      toBeDeletedCols: clone(toBeDeletedCols),
    };

    return spreadInfo;
  }

  private prepareData(initialTables: LookupSetup[], scenarios: Scenario[], scenarioMnemonicsToShow: string[]): LookupSetup[] {
    const finalTables: LookupSetup[] = [];

    initialTables.forEach((table: LookupSetup) => {
      table.Dimensions = table.DimensionList.length;

      // Get table dimensions ((e.g. ['Mnemonic', 'AccountNum', 'ScenarioMn'])
      table.dimensions = [];
      table.DimensionList.forEach(dimension => {
        table.dimensions.push(dimension.DimMnemonic);
      });

      // Get table dimension references (e.g. ['D1_Text', 'D2_Numeric', 'D3_Text'])
      table.references = [];
      table.DimensionList.forEach(dimension => {
        const dimensionNumber = dimension.DimensionNumber;
        const dataType = capitalize(dimension.DataType);
        const reference: any = `D${dimensionNumber}_${dataType}`;
        table.references.push(reference);
      });

      const [d1Reference, d2Reference, d3Reference] = table.references;

      // When storing lookupdata as text, we also need to set the dimension numeric when saving
      const rowOrder = d1Reference.match(/Text/i) ? 'D1_Numeric' : null;
      let colOrder = null,
        pivotOrder = null;
      if (table.Dimensions > 1) {
        colOrder = d2Reference.match(/Text/i) ? 'D2_Numeric' : null;
        if (table.Dimensions > 2) {
          pivotOrder = d3Reference.match(/Text/i) ? 'D3_Numeric' : null;
        }
      }

      const scenMnIndex = table.dimensions.indexOf('ScenarioMn');
      if (scenMnIndex !== -1) {
        const scenReference = table.references[scenMnIndex];
        // If table is dimensioned by Scenario, filter its Data to only show the one related to the Scenario Mnemonics we
        // want to show.
        table.Data = table.Data.filter(d => scenarioMnemonicsToShow.map(m => m.toLowerCase()).includes(d[scenReference].toLowerCase()));
      }
      finalTables.push(table);

      // Iterate over Balance Assumptions tables and set their D3_Numeric to be the corresponding Scenario_id
      if (table.DataTableType === 'BALANCESHEET') {
        table.Data.forEach((dataElement, index) => {
          const scenMnemonic = dataElement[d3Reference];
          const scen = scenarios.find(s => s.mnemonic === scenMnemonic);
          const scenIndex = scen ? scen.scenario_id : index;
          dataElement[pivotOrder] = scenIndex;
        });
      }

      // Sorting dimensions
      if (table.Dimensions === 3) {
        table.Data.sort(compareKeys([pivotOrder || d3Reference, rowOrder || d1Reference, colOrder || d2Reference]));
      } else if (table.Dimensions === 2) {
        table.Data.sort(compareKeys([rowOrder || d1Reference, colOrder || d2Reference]));
      } else {
        table.Data.sort(compareKeys([rowOrder || d1Reference]));
      }
      table.Data.forEach((dataItem, index) => {
        dataItem.Mnemonic_Id = table.Id;
        delete dataItem.Id;
      });
    });
    return finalTables;
  }

  private minify(query: LookupData[]) {
    const results = query.map(q => ({
      m: q.Mnemonic,
      i: q.Mnemonic_Id,
      s: q.ScenarioNum,
      r: q.Result,
      d1n: +q.D1_Numeric,
      d1t: q.D1_Text,
      d2n: +q.D2_Numeric,
      d2t: q.D2_Text,
      d3n: +q.D3_Numeric,
      d3t: q.D3_Text,
      d1d: q.D1_Date,
      d2d: q.D2_Date,
      d3d: q.D3_Date,
      tbd: q.toBeDeleted,
    }));

    results.forEach(result => {
      Object.keys(result).forEach(k => {
        if (result[k] === null || result[k] === '' || result[k] === undefined || result[k] === false) {
          delete result[k];
        }
      });
    });

    return results;
  }

  private deminify(query: any[]) {
    const results = query.map(q => ({
      ScenarioNum: q.s,
      Result: q.r,
      D1_Numeric: q.d1n,
      D1_Text: q.d1t,
      D2_Numeric: q.d2n,
      D2_Text: q.d2t,
      D3_Numeric: q.d3n,
      D3_Text: q.d3t,
      D1_Date: q.d1d,
      D2_Date: q.d2d,
      D3_Date: q.d3d,
      toBeDeleted: q.tbd,
    }));

    results.forEach(result => {
      Object.keys(result).forEach(k => {
        if (result[k] === null || result[k] === '' || result[k] === undefined || result[k] === false) {
          delete result[k];
        }
      });
    });

    return results;
  }
}
